import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowRight, CheckCircle, AlertCircle, Check } from 'lucide-react';
import { fetchPageBySlug } from '../lib/api';

function AnimatedText({ text, startDelay = 0, speed = 0.04 }) {
  return (
    <span className="inline-block">
      {text.split('').map((char, index) => (
        <span
          key={index}
          className="inline-block opacity-0 animate-fade-in-left-letter"
          style={{
            animationDelay: `${startDelay + index * speed}s`,
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
}

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimationFinished, setIsAnimationFinished] = useState(false);

  const sliderImages = [
    'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/image_29.png',
    'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/image_28.png',
    'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/image_31.png'
  ];

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Dan-Team stainless";
    
    const setMetaTag = (attrName, attrValue, content) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMetaTag('name', 'description', '');
    setMetaTag('property', 'og:title', 'Dan-Team stainless');
    setMetaTag('property', 'og:description', '');
    setMetaTag('property', 'og:image', '');
  }, []);

  useEffect(() => {
    async function loadData() {
      const data = await fetchPageBySlug('index');
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { innerWidth, innerHeight } = window;
      // Calculate distance from center of screen (-0.5 to 0.5)
      const x = (e.clientX / innerWidth) - 0.5;
      const y = (e.clientY / innerHeight) - 0.5;
      // Max movement of 15px with smooth transition
      setMouseOffset({ x: x * 15, y: y * 15 });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 12000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimationFinished(true);
    }, 2100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ type: null, message: '' });

    // Simple validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setFormStatus({ type: 'error', message: 'Venligst udfyld alle påkrævede felter.' });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.'
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        throw new Error('Server error');
      }
    } catch (error) {
      console.error('Fejl ved afsendelse af formular:', error);
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl under afsendelsen. Venligst prøv igen eller kontakt os direkte via telefon.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0073A5]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-600">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[550px] flex items-center justify-center overflow-hidden">
        {/* Zoom Slider Background */}
        <div className="absolute inset-0 overflow-hidden">
          {sliderImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[3000ms] ease-in-out ${
                index === currentSlide 
                  ? 'opacity-100 z-10 animate-ken-burns' 
                  : 'opacity-0 z-0'
              }`}
              style={{ 
                backgroundImage: `url(${image})`,
              }}
            />
          ))}
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/65 z-20" />

        {/* Content */}
        <div className="relative z-30 max-w-7xl mx-auto px-6 lg:px-8 text-center w-full space-y-6">
          <h1 
            style={{ transform: `translate(${mouseOffset.x}px, ${mouseOffset.y}px)` }}
            className="font-bold tracking-[0.05em] sm:tracking-[0.15em] uppercase text-white max-w-5xl mx-auto leading-tight transition-transform duration-300 ease-out break-words"
          >
            <span className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white/75 block mb-2 font-medium">
              <AnimatedText text="Velkommen til" startDelay={0.1} speed={0.04} />
            </span>
            <span className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl block font-extrabold whitespace-nowrap">
              <AnimatedText text="Dan-team stainless aps" startDelay={0.6} speed={0.04} />
            </span>
          </h1>

          <div className={`pt-6 flex flex-col items-center justify-center gap-6 transition-all duration-300 ${
            isAnimationFinished ? 'animate-fade-in-down' : 'opacity-0'
          }`}>
            <Link 
              to="/kontakt" 
              className="border-2 border-white bg-transparent px-12 py-5 text-sm md:text-base font-bold tracking-widest text-white uppercase transition-colors duration-200 hover:bg-white hover:text-slate-950"
            >
              Kontakt os
            </Link>
          </div>
        </div>

        {/* Bottom Text with Inline Indicators */}
        <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 z-30 flex items-center justify-center gap-4 text-white text-base sm:text-lg md:text-xl lg:text-2xl px-6 w-full max-w-6xl lg:max-w-7xl font-light uppercase whitespace-normal md:whitespace-nowrap transition-all duration-300 ${
          isAnimationFinished ? 'animate-fade-in-up' : 'opacity-0'
        }`}>
          <span className="tracking-[0.25em] text-center">
            Specialister i fremstilling og montage af <strong className="font-extrabold text-white">rustfrie anlæg</strong>
          </span>
        </div>

        {/* Slider Indicators - Moved completely to the bottom left of the hero */}
        <div className={`absolute bottom-20 left-6 lg:left-12 z-20 flex items-center gap-3 transition-opacity duration-1000 ${
          isAnimationFinished ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}>
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-white scale-110' : 'bg-white/30 hover:bg-white/60'
              }`}
              aria-label={`Gå til dias ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Asymmetric 50/50 Split Layout Section */}
      <section className="bg-white border-b border-slate-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          
          {/* Left Column (Content Panel) */}
          <div className="py-16 px-8 md:px-16 lg:px-24 flex flex-col justify-center bg-white">
            <span className="text-xs font-semibold tracking-widest text-slate-400 uppercase mb-3">
              DAN-TEAM STAINLESS
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-extrabold tracking-wide text-slate-900 leading-tight mb-6 break-words">
              Din professionelle samarbejdspartner i rustfrit stål
            </h2>
            <div className="text-base text-slate-600 leading-relaxed font-normal space-y-4 mb-8">
              <p>
                Dan-Team Stainless er en virksomhed med speciale i fremstilling, montage og supervision af procesanlæg indenfor især fødevare og den kemiske industri.
              </p>
              <p>
                Vi tager gerne hele montageopgaven med vores hold af uddannede og certificerede medarbejder, som arbejder både i Danmark men som også gennem mange år har haft store montageopgaver rundt i hele verden.
              </p>
              <p>
                Siden Dan-Team Stainless startede i 1999 har vi bygget en lang række forskellige procesanlæg for blandt andet SPX Anhydro, GEA Niro, Gea Liquid Scandinavia, Carlisle Process Systems, Siccadania, Orkla Foods og Tetra Pak.
              </p>
              <ul className="space-y-3 text-slate-600 font-semibold">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#0073A5] shrink-0 mt-0.5" />
                  <span>
                    DAN-TEAM er specialister i komplette montager af rustfrie anlæg: Mejerier – bryggerier – fødevareindustrien – den kemiske industri + supervision worldwide
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#0073A5] shrink-0 mt-0.5" />
                  <span>
                    På DAN-TEAMs værksted fremstilles mange af komponenterne til egne montager: Tanke – units – platforme – ventilbatterier + special løsninger
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#0073A5] shrink-0 mt-0.5" />
                  <span>
                    DAN-TEAM er derudover også underleverandører for flere ingeniørfirmaer.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column (Single Big Image with 2x2 overlaid focus boxes) */}
          <div 
            className="relative min-h-[500px] lg:min-h-full bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/66d9d716-76df-484b-a096-f8b2cc4a508f.jpg')` 
            }}
          >
            {/* 2x2 Grid of focus boxes with semi-transparent overlays */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 h-full min-h-[500px] lg:absolute lg:inset-0">
              
              {/* Box 1: Værkstedet */}
              <Link 
                to="/vaerkstedet" 
                className="group relative flex flex-col justify-center items-center text-center p-8 md:p-12 transition-all duration-300 border-r border-b border-white/10 bg-[#0073A5]/75 hover:bg-[#0073A5]/60"
              >
                <div className="space-y-2">
                  <h3 className="text-lg md:text-2xl font-bold tracking-wider text-white uppercase group-hover:text-white transition-colors">
                    Værkstedet
                  </h3>
                </div>
              </Link>

              {/* Box 2: Montageafdelingen */}
              <Link 
                to="/montageafdelingen" 
                className="group relative flex flex-col justify-center items-center text-center p-8 md:p-12 transition-all duration-300 border-b border-white/10 bg-black/45 hover:bg-black/30"
              >
                <div className="space-y-2">
                  <h3 className="text-lg md:text-2xl font-bold tracking-wider text-white uppercase group-hover:text-white transition-colors">
                    Montageafd.
                  </h3>
                </div>
              </Link>

              {/* Box 3: Vandskæring */}
              <Link 
                to="/vandskaering" 
                className="group relative flex flex-col justify-center items-center text-center p-8 md:p-12 transition-all duration-300 border-r border-white/10 bg-black/45 hover:bg-black/30"
              >
                <div className="space-y-2">
                  <h3 className="text-lg md:text-2xl font-bold tracking-wider text-white uppercase group-hover:text-white transition-colors">
                    Vandskæring
                  </h3>
                </div>
              </Link>

              {/* Box 4: Referencer */}
              <Link 
                to="/referencer" 
                className="group relative flex flex-col justify-center items-center text-center p-8 md:p-12 transition-all duration-300 bg-[#0073A5]/75 hover:bg-[#0073A5]/60"
              >
                <div className="space-y-2">
                  <h3 className="text-lg md:text-2xl font-bold tracking-wider text-white uppercase group-hover:text-white transition-colors">
                    Referencer
                  </h3>
                </div>
              </Link>

            </div>
          </div>

        </div>
      </section>

      {/* Alternating 50/50 Layouts: Section 1 (Om DAN-TEAM STAINLESS) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-slate-50">
        {/* Left: Full Bleed Image */}
        <div className="relative min-h-[400px] lg:min-h-full">
          <img 
            src="https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/ff2e3a24-e067-4a7a-8ca9-6c9ce8e48749.png" 
            alt="DAN-TEAM STAINLESS anlæg og produktion" 
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Right: Text Block */}
        <div className="p-12 sm:p-20 lg:p-28 flex flex-col justify-center space-y-6">
          <h2 className="font-bold text-xl sm:text-2xl md:text-4xl text-slate-900 tracking-normal">Om DAN-TEAM STAINLESS</h2>
          
          <div className="text-slate-600 leading-relaxed text-base space-y-4 font-light" data-dev-id="src/pages/Home.jsx:266:10">
            <p>
              Dan-Team Stainless drives i dag af to ejere; Søren Pedersen og Martin Vedel Bindesbøl. Begge har været i branchen for produktion og montage af rustfrit stål i fødevareindustrien, igennem rigtig mange år. Martin står til dels for montage og supervision, mens Søren er leder af værkstedet.
            </p>
            <p className="font-semibold text-slate-900 pt-2">
              Kompetent team
            </p>
            <p>
              Alle medarbejdere er håndplukkede og certificerede efter de gældende krav til opgaverne. Alle arbejder selvstændigt, ud fra tegningsmateriale og PI diagrammer.
            </p>
            <p>
              Derudover er alle medarbejder fortrolige med de strenge krav og standarder, der stilles til hygiejne og finish indenfor især fødevareindustrien. Herudover er det en selvfølge, at alle arbejder for at overholde de kundespecificerede krav.
            </p>
          </div>

          <div className="pt-4">
            <Link 
              to="/om-os" 
              className="bg-[#0073A5] hover:bg-[#005c84] px-6 py-3 text-sm font-bold tracking-wider text-white uppercase transition-colors duration-200 inline-block"
            >
              Læs mere om os
            </Link>
          </div>
        </div>
      </section>

      {/* Overlay Contact Form Container (Full Width Background Image with Parallax & Dark Overlay) */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat bg-fixed py-24 sm:py-32" 
        style={{ backgroundImage: `url('https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/2b5ed721-9e94-481d-a70d-6fff6ada550e.jpg')` }}
      >
        {/* Absolute dark overlay background with 50% opacity */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 flex justify-start">
          <div className="max-w-xl text-white">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-wider mb-4">Få et uforpligtende tilbud</h2>
            <p className="text-base text-slate-100 mb-8 font-normal leading-relaxed">
              Kontakt os og få et uforpligtende tilbud på din opgave. Du kan ringe til os på <a href="tel:+4576991020" className="font-bold text-white hover:underline underline-offset-4">(+45) 76 99 10 20</a> eller udfylde formularen og vi vil vende tilbage til dig hurtigst muligt.
            </p>
            
            <form onSubmit={handleFormSubmit} className="space-y-4">
              {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Website URL</label>
                <input
                  id="website_url"
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input 
                  type="text" 
                  name="name"
                  required
                  placeholder="Navn" 
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-slate-200/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0073A5]"
                />
                <input 
                  type="tel" 
                  name="phone"
                  required
                  placeholder="Tlf. nr." 
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-slate-200/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0073A5]"
                />
              </div>
              
              <input 
                type="email" 
                name="email"
                required
                placeholder="Email" 
                value={formData.email}
                onChange={handleInputChange}
                className="w-full bg-slate-200/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0073A5]"
              />
              
              <textarea 
                name="description"
                required
                placeholder="Besked" 
                rows="4" 
                value={formData.description}
                onChange={handleInputChange}
                className="w-full bg-slate-200/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0073A5] resize-none"
              ></textarea>
              
              {/* Status Messages */}
              {formStatus.type && (
                <div className={`p-4 text-sm ${
                  formStatus.type === 'success' 
                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' 
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}>
                  <span>{formStatus.message}</span>
                </div>
              )}

              <button 
                type="submit" 
                disabled={submitting}
                className="w-full md:w-auto bg-[#0073A5] hover:bg-[#005c84] px-8 py-3 font-bold uppercase text-white transition-colors cursor-pointer"
              >
                {submitting ? 'Sender...' : 'Send besked'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}